import {
  CbhIcon,
  ScrollingNumberCounter,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import pluralize from "pluralize";
import { useEffect, useState } from "react";
import Confetti from "react-confetti";

import graduationCapImage from "./assets/images/graduationCap.png";
import { SCHOLARSHIP_REQUIRED_SHIFTS, SCHOLARSHIP_STATUS_GRADIENT } from "./constants";

const COUNTER_ANIMATION_DURATION_MS = 1500;

interface ScholarshipStatusBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  completedShifts: number;
  shiftsRemaining: number;
  onContinue: () => void;
}

export function ScholarshipStatusBottomSheet(props: ScholarshipStatusBottomSheetProps) {
  const { modalState, workerId, completedShifts, shiftsRemaining, onContinue } = props;

  const [startAnimation, setStartAnimation] = useState(false);
  const hasQualified = completedShifts >= SCHOLARSHIP_REQUIRED_SHIFTS;

  useEffect(() => {
    if (modalState.modalIsOpen) {
      setStartAnimation(false);
      const timer = setTimeout(() => {
        setStartAnimation(true);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [modalState.modalIsOpen]);

  useLogEffect(
    APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_STATUS_VIEWED,
    {
      workerId,
      completedShifts,
      shiftsRemaining,
    },
    { enabled: modalState.modalIsOpen }
  );

  const handleContinueClick = (): void => {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_STATUS_CONTINUE_CLICKED, {
      workerId,
      completedShifts,
      shiftsRemaining,
    });
    onContinue();
  };

  return (
    <FullScreenDialog
      modalState={modalState}
      variant="tertiary"
      PaperProps={{
        sx: {
          background: SCHOLARSHIP_STATUS_GRADIENT,
          overflow: "hidden",
        },
      }}
    >
      {hasQualified && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={400}
          gravity={0.8}
          recycle={false}
        />
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hasQualified ? (
          <Stack spacing={6} alignItems="center" sx={{ paddingX: 5, textAlign: "center" }}>
            <Box
              component="img"
              src={graduationCapImage}
              alt="Clipboard Health mascot"
              sx={{
                width: 120,
                height: 120,
                objectFit: "contain",
              }}
            />

            <Text
              semibold
              variant="h3"
              textAlign="center"
              sx={{ color: (theme) => theme.palette.text.primary }}
            >
              Congrats, you&apos;ve qualified for 15% off tuition!
            </Text>

            <Text
              variant="body1"
              textAlign="center"
              sx={{ color: (theme) => theme.palette.text?.secondary ?? "", paddingX: 4 }}
            >
              We&apos;ll be in touch with details on how to redeem your discount soon.
            </Text>
          </Stack>
        ) : (
          <Stack spacing={9} sx={{ paddingTop: 10, paddingX: 5, textAlign: "center" }}>
            <Stack spacing={6} alignItems="center">
              <Box
                component="img"
                src={graduationCapImage}
                alt="Clipboard Health mascot"
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "contain",
                }}
              />
              <Box
                sx={{
                  transform: "scale(2.2)",
                  transformOrigin: "center",
                  paddingY: 8,
                }}
              >
                <ScrollingNumberCounter
                  startValue={SCHOLARSHIP_REQUIRED_SHIFTS}
                  endValue={startAnimation ? shiftsRemaining : SCHOLARSHIP_REQUIRED_SHIFTS}
                  duration={COUNTER_ANIMATION_DURATION_MS}
                  variant="h1"
                  color="rgba(0, 67, 69, 1)"
                />
              </Box>

              <Text
                variant="body1"
                textAlign="center"
                sx={{
                  color: (theme) => theme.palette.text?.secondary ?? "",
                  paddingX: 4,
                }}
              >
                more {pluralize("shift", shiftsRemaining)} before April 15 to earn 15% tuition
                discount!
              </Text>
            </Stack>

            <Stack spacing={4} sx={{ paddingX: 2 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <CbhIcon
                  type="checkmark"
                  size="xLarge"
                  color={(theme) => theme.palette.primary.main}
                />
                <Text variant="body1">
                  {completedShifts} of {SCHOLARSHIP_REQUIRED_SHIFTS} shifts completed
                </Text>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={3}>
                <CbhIcon
                  type="checkmark"
                  size="xLarge"
                  color={(theme) => theme.palette.primary.main}
                />
                <Text variant="body1">Have zero No Show during this period</Text>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Box>
      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          data-testid="scholarship-status-continue-button"
          onClick={handleContinueClick}
        >
          Continue
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
